// Shared loader + head for the "Alle produkter" / "All products" routes.
// Renders the existing CollectionPage by feeding it a collection object —
// either Shopify's automatic `all` collection, or a synthetic one built from
// fetchAllProducts() as a fallback.

import {
  fetchCollection,
  fetchAllProducts,
  type ShopifyProduct,
  type CollectionWithProducts,
} from "@/lib/shopify";
import { SITE_URL, localizedHead, breadcrumbJsonLd, collectionPageJsonLd } from "@/lib/seo";

export const allProductsLoader = async () => {
  // Shopify auto-generates an `all` collection that lists every published
  // product. Try it first — it gives us a real collection object (title,
  // image, etc.) for free.
  let collection: CollectionWithProducts | null = null;
  try {
    collection = await fetchCollection("all", 250);
  } catch {
    collection = null;
  }

  if (!collection || collection.products.edges.length === 0) {
    // Fallback: page through products and build a synthetic collection so
    // the page never ends up empty if the auto-`all` is hidden on this store.
    const products: ShopifyProduct[] = await fetchAllProducts({ max: 500 });
    collection = {
      id: "synthetic:all",
      handle: "alle",
      title: "Alle produkter",
      description:
        "Hele Farveladelands katalog samlet på ét sted — Murano-lamper, borddækning, glas og loppefund.",
      image: null,
      products: { edges: products },
    } as CollectionWithProducts;
  }

  return { collection };
};

type LoaderData = { collection: CollectionWithProducts };

function buildHead(loaderData: LoaderData | undefined, locale: "da" | "en") {
  const c = loaderData?.collection;
  const path = locale === "en" ? "/en/alle" : "/alle";
  const url = `${SITE_URL}${path}`;
  const count = c?.products.edges.length ?? 0;

  const title =
    locale === "en"
      ? "All products — Vintage at Farveladeland"
      : "Alle produkter — Vintage hos Farveladeland";
  const description =
    locale === "en"
      ? `Browse every piece in the Farveladeland shop — ${count > 0 ? `${count} hand-picked vintage finds, ` : ""}from Murano lamps to tableware and glassware.`
      : `Browse hele Farveladelands udvalg — ${count > 0 ? `${count} håndplukkede vintage-fund, ` : ""}fra Murano-lamper til borddækning og glas.`;

  const ogImage = c?.image?.url || (c?.products.edges[0] as ShopifyProduct | undefined)?.node?.images?.edges?.[0]?.node?.url;

  const ld = c
    ? collectionPageJsonLd({
        name: locale === "en" ? "All products" : "Alle produkter",
        description,
        url,
        image: ogImage ?? undefined,
      })
    : null;
  const crumb = breadcrumbJsonLd([
    { name: locale === "en" ? "Home" : "Forsiden", url: SITE_URL },
    { name: locale === "en" ? "All products" : "Alle produkter", url },
  ]);
  const loc = localizedHead({ path: "/alle", locale });

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      ...(ogImage ? [{ property: "og:image", content: ogImage }] : []),
      ...(ogImage ? [{ name: "twitter:image", content: ogImage }] : []),
      ...loc.meta,
    ],
    links: loc.links,
    scripts: [
      ...(ld ? [{ type: "application/ld+json", children: JSON.stringify(ld) }] : []),
      { type: "application/ld+json", children: JSON.stringify(crumb) },
    ],
  };
}

export const allProductsHeadDa = ({ loaderData }: any) =>
  buildHead(loaderData, "da");
export const allProductsHeadEn = ({ loaderData }: any) =>
  buildHead(loaderData, "en");
